/** FIX-286: donor appliance + harvest work order + part category for every new part. */

export type DonorField = "workOrder" | "appliance" | "category" | "brand" | "model" | "serial";

export interface DonorAppliance {
  id: string;
  category: string | null;
  brand: string | null;
  model_number: string | null;
  serial_number: string | null;
}

export interface DonorFields {
  harvest_work_order_id: string;
  appliance_id: string;
  donor_appliance_category: string;
  brand: string;
  model_number: string;
  donor_serial_number: string;
}

const clean = (v: string | null | undefined) => (v ?? "").trim();

export function donorFromAppliance(
  appliance: DonorAppliance | null | undefined,
  workOrderId: string | null | undefined,
): { ok: true; fields: DonorFields } | { ok: false; missing: DonorField[] } {
  const missing: DonorField[] = [];
  if (!clean(workOrderId)) missing.push("workOrder");
  if (!appliance?.id) {
    missing.push("appliance");
    return { ok: false, missing };
  }
  if (!clean(appliance.category)) missing.push("category");
  if (!clean(appliance.brand)) missing.push("brand");
  if (!clean(appliance.model_number)) missing.push("model");
  if (!clean(appliance.serial_number)) missing.push("serial");
  if (missing.length) return { ok: false, missing };
  return {
    ok: true,
    fields: {
      harvest_work_order_id: clean(workOrderId),
      appliance_id: appliance.id,
      donor_appliance_category: clean(appliance.category),
      brand: clean(appliance.brand),
      model_number: clean(appliance.model_number),
      donor_serial_number: clean(appliance.serial_number),
    },
  };
}

/** Rules-sheet Category for a harvested item; "Custom" for custom components. */
export function partCategoryFor(c: { id: string; name: string; material?: string | null; category?: string | null }): string | null {
  if (c.id.startsWith("custom-")) return "Custom";
  if (c.id.startsWith("rec:") || c.id.startsWith("rec-")) {
    const m = clean(c.material);
    return m && m !== "Unknown" ? m : null;
  }
  return clean(c.category) || clean(c.name) || null;
}

/** Builds the donor + category part of a spare_parts insert, or reports what is missing. */
export function sparePartDonorRow(
  appliance: DonorAppliance | null | undefined,
  workOrderId: string | null | undefined,
  item: { id: string; name: string; material?: string | null; category?: string | null },
): { ok: true; row: DonorFields & { part_category: string } } | { ok: false; missing: (DonorField | "partCategory")[] } {
  const donor = donorFromAppliance(appliance, workOrderId);
  const cat = partCategoryFor(item);
  if ("missing" in donor) return { ok: false, missing: cat ? donor.missing : [...donor.missing, "partCategory"] };
  if (!cat) return { ok: false, missing: ["partCategory"] };
  return { ok: true, row: { ...donor.fields, part_category: cat } };
}

/** Parts listed on the "No part type" tab. */
export function partsWithoutType<T extends { part_type_id: string | null }>(parts: T[]): T[] {
  return parts.filter((p) => !p.part_type_id);
}
